import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStatsRefresh } from '../../context/StatsRefreshContext';

interface StatBarProps {
    label: string;
    value: number;
    total: number;
    color: string;
}

const StatBar: React.FC<StatBarProps> = ({ label, value, total, color }) => {
    const [width, setWidth] = useState(0);
    const pct = total > 0 ? Math.round((value / total) * 100) : 0;

    useEffect(() => {
        const t = setTimeout(() => setWidth(pct), 60);
        return () => clearTimeout(t);
    }, [pct]);

    return (
        <div className="sidebar-stat-row">
            <div className="sidebar-stat-header">
                <span className="sidebar-stat-label">{label}</span>
                <span className="sidebar-stat-value">{value.toLocaleString()}</span>
            </div>
            <div className="sidebar-stat-track">
                <div
                    className="sidebar-stat-fill"
                    style={{
                        width: `${width}%`,
                        backgroundColor: color,
                        transition: 'width 0.55s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                />
            </div>
            <div className="sidebar-stat-pct">{pct}%</div>
        </div>
    );
};

const Progress = () => {
    const { t } = useTranslation('statistics');
    const { overview, overviewLoading } = useStatsRefresh();
    const loading = overviewLoading;
    const error = !overviewLoading && !overview ? t('progress.load_error') : null;
    const stats = overview ? {
        totalWords: overview.total_words,
        wordsMastered: overview.words_mastered,
        wordsInProgress: overview.words_in_progress,
        masteryPercentage: overview.mastery_percent,
        streakDays: overview.streak_days,
    } : null;
    const reviewStats = overview?.review ?? null;

    if (loading) {
        return (
            <div className="progress-section">
                <h3 className="section-title">{t('progress.title')}</h3>
                <div className="sidebar-skeleton">
                    <div className="skeleton-ring" />
                    <div className="skeleton-bars">
                        <div className="skeleton-bar" />
                        <div className="skeleton-bar" />
                        <div className="skeleton-bar" />
                    </div>
                </div>
            </div>
        );
    }

    if (error || !stats || !reviewStats) {
        return (
            <div className="progress-section">
                <h3 className="section-title">{t('progress.title')}</h3>
                <p className="sidebar-error">{error || t('progress.no_data')}</p>
            </div>
        );
    }

    const circumference = 2 * Math.PI * 34;
    const strokeDashoffset = circumference - (stats.masteryPercentage / 100) * circumference;
    const total = stats.totalWords;

    return (
        <div className="progress-section">
            <div className="progress-header-row">
                <div>
                    <h3 className="section-title" style={{ marginBottom: 4 }}>{t('progress.title')}</h3>
                    <span className="progress-total-label">{t('progress.total', { count: total })}</span>
                </div>
                <div className="progress-ring-mini">
                    <svg viewBox="0 0 80 80" width="80" height="80">
                        <circle cx="40" cy="40" r="34" className="progress-circle-bg" />
                        <circle
                            cx="40" cy="40" r="34"
                            className="progress-circle-fill"
                            strokeDasharray={circumference}
                            strokeDashoffset={strokeDashoffset}
                            style={{ transform: 'rotate(-90deg)', transformOrigin: '40px 40px' }}
                        />
                    </svg>
                    <div className="progress-ring-center">
                        <span className="progress-percentage-sm">{stats.masteryPercentage}%</span>
                        <span className="progress-label-sm">{t('progress.mastery')}</span>
                    </div>
                </div>
            </div>

            <div className="sidebar-stats-bars">
                <StatBar
                    label={t('progress.mastered')}
                    value={stats.wordsMastered}
                    total={total}
                    color="var(--green)"
                />
                <StatBar
                    label={t('progress.in_progress')}
                    value={stats.wordsInProgress}
                    total={total}
                    color="var(--orange)"
                />
                <StatBar
                    label={t('progress.due_today')}
                    value={reviewStats.due_today}
                    total={total}
                    color="var(--primary-color)"
                />
            </div>
        </div>
    );
};

export default Progress;
